import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "./api";

const STORAGE_KEY = "wnbf-admin-token";

const AdminContext = createContext({
  token: null,
  admin: null,
  loading: false,
  login: async () => {},
  logout: () => {},
  authedClient: null,
});

const loadToken = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

export const AdminProvider = ({ children }) => {
  const [token, setToken] = useState(loadToken);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(false);

  const authedClient = useMemo(() => {
    const c = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" }, timeout: 15000 });
    c.interceptors.request.use((config) => {
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    c.interceptors.response.use(
      (resp) => resp,
      (error) => {
        if (error?.response?.status === 401) {
          setToken(null);
          setAdmin(null);
          try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
        }
        return Promise.reject(error);
      }
    );
    return c;
  }, [token]);

  // Verify token on mount/change
  useEffect(() => {
    let alive = true;
    if (!token) {
      setAdmin(null);
      return () => { alive = false; };
    }
    setLoading(true);
    authedClient
      .get("/admin/me")
      .then((res) => alive && setAdmin(res.data))
      .catch(() => alive && setAdmin(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token, authedClient]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/admin/login`, { email, password });
    setToken(data.access_token);
    try { window.localStorage.setItem(STORAGE_KEY, data.access_token); } catch { /* noop */ }
    return data;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setAdmin(null);
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  }, []);

  return (
    <AdminContext.Provider value={{ token, admin, loading, login, logout, authedClient }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => useContext(AdminContext);
